import type { TFunction } from 'i18next';

import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import {
  downloadChronicleTimelinePdf,
  downloadNarrativeReportPdf,
  downloadReportPdf,
} from '@/utils/report-pdf';
import { formatChronicleDate, formatDateTime } from '@/utils/format';

import { fetchReportForExport } from './fetch-report-for-export';
import {
  formatReportExportCell,
  formatReportTotalsLines,
  translateReportValue,
  visibleExportColumns,
} from './format-report-export';

export const downloadReportPdfExport = async (input: {
  reportKey: string;
  reportTitle: string;
  reportDescription: string;
  churchName: string;
  branchName?: string | null;
  logoUrl?: string | null;
  baseCurrency: string;
  dateFrom?: string;
  dateTo?: string;
  filters?: Record<string, string>;
  supportsDateRange: boolean;
  locale: string;
  timezone: string;
  t: TFunction<'reports'>;
  tCommon: TFunction;
}): Promise<void> => {
  const data = await fetchReportForExport({
    reportKey: input.reportKey,
    dateFrom: input.dateFrom,
    dateTo: input.dateTo,
    filters: input.filters,
  });

  const columns = visibleExportColumns(data.columns, data.rows).filter(
    (column) => !(input.reportKey === 'chronicle_moments' && column.key === 'datePrecision'),
  );
  const columnLabels = columns.map((column) => input.t(`column.${column.key}`, column.key));
  const rows = data.rows.map((row) =>
    columns.map((column) =>
      formatReportExportCell(column, row[column.key], row, input.t, { locale: input.locale }),
    ),
  );

  const periodLabel =
    input.supportsDateRange && (input.dateFrom || input.dateTo)
      ? input.t('run.pdfPeriod', {
          from: input.dateFrom ?? input.tCommon('date.allTime'),
          to: input.dateTo ?? input.tCommon('date.allTime'),
        })
      : undefined;

  await downloadReportPdf({
    churchName: input.churchName,
    footerLabel: input.tCommon('app.fullName'),
    branchName: input.branchName,
    logoUrl: input.logoUrl,
    title: input.reportTitle,
    description: input.reportDescription,
    periodLabel,
    generatedLabel: input.t('run.pdfGenerated', {
      when: formatDateTime(new Date().toISOString(), {
        locale: input.locale,
        timezone: input.timezone,
        withZone: true,
      }),
    }),
    omittedColumnsNote: data.omittedColumns.length ? input.t('run.omittedColumns') : undefined,
    columns,
    columnLabels,
    rows,
    totalsLines: formatReportTotalsLines(data.totals, input.t, input.baseCurrency),
    filename: `${input.reportKey}.pdf`,
  });

  try {
    await api.post<void>(`/reports/${input.reportKey}/export`, {
      format: 'pdf',
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      ...input.filters,
    });
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(500);
  }
};

const recordChroniclePdfExport = async (input: {
  reportKey: string;
  dateFrom?: string;
  dateTo?: string;
  filters?: Record<string, string>;
}) => {
  try {
    await api.post<void>(`/reports/${input.reportKey}/export`, {
      format: 'pdf',
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      ...input.filters,
    });
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(500);
  }
};

const chronicleMomentFields = (
  row: Record<string, unknown>,
  t: TFunction<'reports'>,
  locale: string,
) => {
  const precision = row.datePrecision;
  const occurredOn = typeof row.occurredOn === 'string' ? row.occurredOn : '';
  const dateLabel =
    precision === 'day' || precision === 'month' || precision === 'year'
      ? formatChronicleDate(occurredOn, precision, { locale })
      : occurredOn;
  const category = typeof row.categoryName === 'string' ? row.categoryName : '';
  const status = typeof row.status === 'string' ? translateReportValue(t, row.status) : '';
  const visibility =
    typeof row.visibility === 'string' ? translateReportValue(t, row.visibility) : '';
  const body = typeof row.body === 'string' ? row.body.trim() : '';
  const title = typeof row.title === 'string' ? row.title.trim() : '';
  return {
    year: occurredOn.slice(0, 4) || '—',
    dateLabel: [dateLabel, category].filter((part) => part.length > 0).join(' · '),
    title: title || '—',
    meta: [status, visibility].filter((part) => part.length > 0).join(' · '),
    body,
  };
};

const chronicleGeneratedLabel = (input: {
  t: TFunction<'reports'>;
  locale: string;
  timezone: string;
}) =>
  input.t('run.pdfGenerated', {
    when: formatDateTime(new Date().toISOString(), {
      locale: input.locale,
      timezone: input.timezone,
      withZone: true,
    }),
  });

/** One chronicle moment as a written report. Photos and files are left out. */
export const downloadChronicleDocumentPdf = async (
  input: Parameters<typeof downloadReportPdfExport>[0],
): Promise<void> => {
  const data = await fetchReportForExport({
    reportKey: input.reportKey,
    filters: input.filters,
  });
  const row = data.rows[0];
  if (!row) return;

  const moment = chronicleMomentFields(row, input.t, input.locale);

  await downloadNarrativeReportPdf({
    churchName: input.churchName,
    footerLabel: input.tCommon('app.fullName'),
    branchName: input.branchName,
    logoUrl: input.logoUrl,
    title: moment.title === '—' ? input.reportTitle : moment.title,
    metaLines: [moment.dateLabel, moment.meta].filter((line) => line.length > 0),
    body: moment.body || '—',
    generatedLabel: chronicleGeneratedLabel(input),
    filename: 'chronicle-moment.pdf',
  });

  await recordChroniclePdfExport(input);
};

/** Chronicle moments for a period, laid out as a timeline. Photos and files are left out. */
export const downloadChronicleTimelinePdfExport = async (
  input: Parameters<typeof downloadReportPdfExport>[0],
): Promise<void> => {
  const data = await fetchReportForExport({
    reportKey: input.reportKey,
    dateFrom: input.dateFrom,
    dateTo: input.dateTo,
    filters: input.filters,
  });

  const periodLabel =
    input.dateFrom || input.dateTo
      ? input.t('run.pdfPeriod', {
          from: input.dateFrom ?? input.tCommon('date.allTime'),
          to: input.dateTo ?? input.tCommon('date.allTime'),
        })
      : undefined;

  await downloadChronicleTimelinePdf({
    churchName: input.churchName,
    footerLabel: input.tCommon('app.fullName'),
    branchName: input.branchName,
    logoUrl: input.logoUrl,
    title: input.reportTitle,
    description: input.t('chronicle.timeline'),
    periodLabel,
    generatedLabel: chronicleGeneratedLabel(input),
    moments: data.rows.map((row) => chronicleMomentFields(row, input.t, input.locale)),
    filename: 'chronicle-timeline.pdf',
  });

  await recordChroniclePdfExport(input);
};
